import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { EXPIRED_FLAG } from '../lib/sessionTimeout';
import InstallApp from '../components/InstallApp';

// New people join by invite only (PHASE_23): a DBA, director or manager
// invites them, and the link in the email (/?invite=<token>) opens the join
// view below, where they choose a password. There is no open sign-up.
const ROLE_LABEL = { executive: 'Partnerships Executive', operations: 'Operations', dba: 'Database Administrator', manager: 'Manager', director: 'Director' };
const inviteToken = () => { try { return new URLSearchParams(window.location.search).get('invite') || ''; } catch { return ''; } };
const clearInviteFromUrl = () => { try { const u = new URL(window.location.href); u.searchParams.delete('invite'); window.history.replaceState(null, '', u.pathname + u.search + u.hash); } catch { /* ignore */ } };

// When Supabase itself is down or overloaded, sign-in fails with an empty or
// technical error ("{}", a 5xx, a network failure) or never answers. Say so
// plainly, so nobody thinks their password is wrong.
const SERVICE_DOWN = "Supabase isn't responding right now. Wait a minute and try again. (This isn't a problem with your password.)";
const TIMEOUT_MS = 20000;
function friendlyAuthError(error) {
  const msg = (error?.message || '').trim();
  const status = error?.status || 0;
  if (!msg || msg === '{}' || status === 0 || status >= 500 || error?.name === 'AuthRetryableFetchError'
      || /fetch|network|timeout|gateway/i.test(msg)) return SERVICE_DOWN;
  return msg;
}
// Give up waiting after TIMEOUT_MS instead of showing "Please wait..." forever.
function withTimeout(promise) {
  return Promise.race([
    promise,
    new Promise(resolve => setTimeout(() => resolve({ error: { message: '', status: 504 } }), TIMEOUT_MS)),
  ]);
}

export default function LoginPage() {
  const [token]               = useState(inviteToken);
  const [mode, setMode]       = useState(token ? 'join' : 'login');
  const [invite, setInvite]   = useState(null);   // { email, full_name, role, invited_by_name, status } | 'invalid'
  const [email, setEmail]     = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const [expired, setExpired] = useState(false);

  // Surface the "session expired due to inactivity" notice. The flag lives in
  // localStorage so it reaches every tab that gets signed out, not just the one
  // where the timeout fired.
  useEffect(() => {
    const check = () => {
      try {
        if (window.localStorage.getItem(EXPIRED_FLAG)) {
          setExpired(true);
          window.localStorage.removeItem(EXPIRED_FLAG);
        }
      } catch (e) { /* ignore */ }
    };
    check();
    window.addEventListener('storage', check);
    return () => window.removeEventListener('storage', check);
  }, []);

  // The invite behind the link: who it is for and whether it still works.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.rpc('invite_details', { p_token: token });
      if (cancelled) return;
      const row = Array.isArray(data) ? data[0] : data;
      if (error || !row) { setInvite('invalid'); return; }
      setInvite(row);
      setEmail(row.email);
    })();
    return () => { cancelled = true; };
  }, [token]);

  function toLogin() { clearInviteFromUrl(); setMode('login'); setError(''); setSuccess(''); setPassword(''); }

  async function handleJoin(e) {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Choose a password of at least 8 characters.'); return; }
    if (password !== password2) { setError('The two passwords are not the same.'); return; }
    setLoading(true);
    const { data, error } = await supabase.functions.invoke('accept-invite', { body: { token, password } });
    if (error) {
      let msg = error.message, status = null;
      try { const b = await error.context.json(); msg = b?.error || msg; status = b?.status || null; } catch { /* keep */ }
      if (error.context?.status === 404 && !status) msg = 'Joining is not switched on yet. Ask whoever invited you to contact the DBA.';
      setLoading(false);
      setError(msg);
      if (status === 'used') setInvite(i => (i && i !== 'invalid' ? { ...i, status: 'used' } : i));
      return;
    }
    // Account created: sign straight in.
    const { error: signErr } = await withTimeout(supabase.auth.signInWithPassword({ email: data?.email || invite.email, password }));
    setLoading(false);
    if (signErr) { setSuccess('Your account is ready. Sign in with your email and new password.'); setMode('login'); clearInviteFromUrl(); return; }
    clearInviteFromUrl();
  }

  async function handleLogin(e) {
    e.preventDefault();
    setLoading(true); setError(''); setExpired(false);
    const { error } = await withTimeout(supabase.auth.signInWithPassword({ email, password }));
    if (error) setError(friendlyAuthError(error));
    setLoading(false);
  }

  return (
    <div className="login-wrap">
      <div className="login-brand">
        <img className="login-logo" src="/brand/logo-mark.png" alt="Momentum Miles" />
        <div>
          <div className="login-headline">Every lead, call and meeting, <em>in one place.</em></div>
          <div className="login-points">
            <div><i>✓</i>Pipeline, sequences and meetings for every market</div>
            <div><i>✓</i>Built-in dialer with call history and voicemail</div>
            <div><i>✓</i>Live scorecards and lead stock for the whole team</div>
          </div>
        </div>
        <div className="login-foot">Momentum Miles · Control Centre</div>
      </div>

      <div className="login-side">
      <div style={{ background:'var(--surface)', borderRadius:18, padding:'38px 36px', width:'100%', maxWidth:420, position:'relative', zIndex:1, boxShadow:'var(--shadow-lg)', border:'1px solid var(--border)' }}>
        {/* Logo */}
        <div style={{ textAlign:'center', marginBottom:28 }}>
          <div style={{ width:64, height:64, background:'var(--deep)', borderRadius:16, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 12px', boxShadow:'0 8px 22px rgba(13,22,38,0.25)' }}>
            <img src="/brand/logo-mark-sm.png" alt="" style={{ width:46, height:'auto', display:'block' }} />
          </div>
          <div style={{ fontFamily:'var(--fh)', fontSize:22, fontWeight:800, color:'var(--navy)', letterSpacing:'-0.4px' }}>{mode==='login' ? 'Welcome back' : 'Join the Control Centre'}</div>
          <div style={{ fontSize:13.5, color:'var(--slate2)', marginTop:4 }}>{mode==='login' ? 'Sign in to Momentum Miles Control Centre' : invite && invite !== 'invalid' && invite.status === 'open' ? 'Choose a password to set up your account' : 'Your invite link'}</div>
        </div>

        {expired && <div style={{ background:'var(--ap)', color:'var(--a-ink)', padding:'10px 13px', borderRadius:8, fontSize:13.5, marginBottom:16, border:'1px solid rgba(245,166,35,0.35)' }}>Your session expired due to inactivity. Please sign in again.</div>}
        {error && <div style={{ background:'var(--rp)', color:'var(--r-ink)', padding:'10px 13px', borderRadius:8, fontSize:13.5, marginBottom:16, border:'1px solid rgba(244,63,94,0.2)' }}>{error}</div>}
        {success && <div style={{ background:'var(--gp)', color:'var(--g-ink)', padding:'10px 13px', borderRadius:8, fontSize:13.5, marginBottom:16, border:'1px solid rgba(16,185,129,0.2)' }}>{success}</div>}

        {mode === 'join' && !invite && <div style={{ textAlign:'center', color:'var(--slate2)', fontSize:13.5, padding:'10px 0 18px' }}>Checking your invite…</div>}

        {mode === 'join' && invite && (invite === 'invalid' || invite.status !== 'open') && (
          <div style={{ textAlign:'center', padding:'4px 0 6px' }}>
            <div style={{ fontSize:14.5, color:'var(--navy)', fontWeight:700, marginBottom:6 }}>
              {invite === 'invalid' ? 'This invite link is not valid.' : invite.status === 'used' ? 'This invite has already been used.' : 'This invite was cancelled.'}
            </div>
            <div style={{ fontSize:13.5, color:'var(--slate2)', marginBottom:18 }}>
              {invite !== 'invalid' && invite.status === 'used' ? 'Your account is set up. Sign in with your email and password.' : 'Ask your director, manager or DBA to send you a new invite.'}
            </div>
            <button type="button" className="btn btn-ghost" onClick={toLogin}>Go to sign in</button>
          </div>
        )}

        {mode === 'join' && invite && invite !== 'invalid' && invite.status === 'open' && (
          <form onSubmit={handleJoin}>
            <div style={{ background:'var(--surface2)', border:'1px solid var(--line)', borderRadius:10, padding:'12px 14px', marginBottom:18, fontSize:13.5, color:'var(--navy)', lineHeight:1.55 }}>
              <strong>{invite.invited_by_name || 'Momentum Miles'}</strong> invited you to join as <strong>{ROLE_LABEL[invite.role] || invite.role}</strong>.
            </div>
            <div style={{ marginBottom:14 }}>
              <label className="form-label">Name</label>
              <input value={invite.full_name} readOnly className="form-input" />
            </div>
            <div style={{ marginBottom:14 }}>
              <label className="form-label">Email</label>
              <input value={invite.email} readOnly className="form-input" />
            </div>
            <div style={{ marginBottom:14 }}>
              <label className="form-label">Choose a password</label>
              <input type="password" value={password} onChange={e=>setPassword(e.target.value)} required minLength={8} autoComplete="new-password" placeholder="At least 8 characters" className="form-input" />
            </div>
            <div style={{ marginBottom:20 }}>
              <label className="form-label">Type it again</label>
              <input type="password" value={password2} onChange={e=>setPassword2(e.target.value)} required autoComplete="new-password" placeholder="••••••••" className="form-input" />
            </div>
            <button type="submit" disabled={loading} style={{ width:'100%', padding:12, background:'linear-gradient(135deg,#F5A623,#E8860A)', border:'none', borderRadius:10, fontFamily:'var(--fh)', fontSize:14.5, fontWeight:800, color:'var(--deep)', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, boxShadow:'0 4px 14px rgba(245,166,35,0.3)' }}>
              {loading ? 'Setting up your account...' : 'Join →'}
            </button>
            <div style={{ textAlign:'center', marginTop:12 }}><button type="button" onClick={toLogin} style={{ background:'none', border:'none', color:'var(--slate2)', fontSize:12.5, cursor:'pointer', textDecoration:'underline' }}>I already have an account</button></div>
          </form>
        )}

        {mode === 'login' && (
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom:14 }}>
              <label className="form-label">Email</label>
              <input type="email" value={email} onChange={e=>setEmail(e.target.value)} required placeholder="you@momentummiles.org" className="form-input" autoComplete="username" />
            </div>
            <div style={{ marginBottom:20 }}>
              <label className="form-label">Password</label>
              <input type="password" value={password} onChange={e=>setPassword(e.target.value)} required placeholder="••••••••" className="form-input" autoComplete="current-password" />
            </div>
            <button type="submit" disabled={loading} style={{ width:'100%', padding:12, background:'linear-gradient(135deg,#F5A623,#E8860A)', border:'none', borderRadius:10, fontFamily:'var(--fh)', fontSize:14.5, fontWeight:800, color:'var(--deep)', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, boxShadow:'0 4px 14px rgba(245,166,35,0.3)', transition:'all 0.2s' }}>
              {loading ? 'Please wait...' : 'Sign In →'}
            </button>
            <div style={{ textAlign:'center', marginTop:14, fontSize:12.5, color:'var(--slate2)' }}>New to the team? Ask your director, manager or DBA for an invite.</div>
          </form>
        )}

        <InstallApp variant="login" />

        <div style={{ textAlign:'center', marginTop:20, fontSize:11.5, color:'var(--slate2)' }}>Momentum Miles Control Centre v1.0</div>
      </div>
      </div>
    </div>
  );
}
